/**
 * Tenant SLA hooks (GH #2390)
 *
 * React Query wrappers over tenantSlaApi: the caller's own tenant SLA
 * thresholds (GET) and their full replace (PUT). A successful save writes the
 * server's response into the query cache, so the page shows what was stored,
 * and invalidates the task queries, whose age bands read these thresholds.
 * A saved configuration is the institution's own, so the cached entry carries
 * `platformDefault` false, as the next GET would (GH #4317).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  tenantSlaApi,
  type TenantSlaConfig,
  type TenantSlaConfigResponse,
} from '../api/services/tenant-sla';
import { taskKeys } from './useTasks';

export const TENANT_SLA_QUERY_KEY = ['admin', 'tenants', 'me', 'sla'] as const;

export function useTenantSla() {
  return useQuery<TenantSlaConfigResponse, Error>({
    queryKey: TENANT_SLA_QUERY_KEY,
    queryFn: () => tenantSlaApi.get(),
  });
}

export function useUpdateTenantSla() {
  const queryClient = useQueryClient();
  return useMutation<TenantSlaConfig, Error, TenantSlaConfig>({
    mutationFn: (body) => tenantSlaApi.put(body),
    onSuccess: (saved) => {
      queryClient.setQueryData<TenantSlaConfigResponse>(TENANT_SLA_QUERY_KEY, {
        ...saved,
        platformDefault: false,
      });
      void queryClient.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}
